import { LoadingOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { LogoLoader } from '../../../../common/components/LogoLoader/LogoLoader';
import { displayMoneyValue } from '../../../../common/utils/number-display.utils';
import { useTrackEvent, useTrackGTMEvent } from '../../../../hooks/useTrackEvent/useTrackEvent';
import { useAnalytics } from '../../../app/data/AnalyticsProvider';
import { useStore } from '../../../app/data/root.store';
import { ComparePlansButton, ContinueButton, DiscountLabelText, DiscountLabelWrapper, HeaderLogoAndTextWrapper, LandingPageSvgWrapper, LogoWrapper, MobileMenuHoverRow, MobileMenuHoverWrapper, MobilePricingModalHeaderWrapper, ModalRightPaneFooter, ModalRightPaneWrapper, PageWrapper, PlanDetailsWrapper, PlanDiscountValue, PlanPriceValue, PlanPriceWrapper, PlanRowInvisibleBorder, PlanRowWrapper, PlanSelectionCircle, PlanSelectionCircleFill, PlanSwitchOption, PlanSwitchWrapper, PlanTitleWrapper, PlansWrapper, RecommendedPlanBanner, SavePercentage, Wrapper } from './MobilePricingModal.styles';
import { ModalCloseButtonWrapper, StartExploringFreePlanContainer } from './PricingModal.styles';
import { Dropdown } from 'antd';
import { startIntercomNewMessage } from '../../../../common/utils/intercom.utils';
import { colors } from '../../../../common/styles/theme.styles';
import { MobileMenuWrapper } from '../MobileCompleteSetupScreen/MobileCompleteSetupScreen.styles';
import { MobileAppPricingModal } from './MobileAppPricingModal';

export const MobilePricingModal = observer(({ onClose }) => {
  const { userStore, billingStore, filtersStore, uiStore } = useStore();
  const mobilePricingModalWrapper = useRef();
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [isFooterSticky, setIsFooterSticky] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); 
  const { trackEvent, mixpanelReset } = useAnalytics()
  const actualTier = userStore.tierIndex;
  let recommendedPlanIndex = userStore.data?.profile?.scoreTier - 1;
  // We need to increase the index by one to the next recommendation if the user's recommended plan is the same as what he ordered
  if (recommendedPlanIndex === actualTier) {
    recommendedPlanIndex += 1
  }

  const [selectedPlanIndex, setSelectedPlanIndex] = useState(userStore.isReadOnlyMode ? actualTier : recommendedPlanIndex || actualTier);

  const plans = [{
    title: 'Starter',
    mixPanelTitle: 'Starter',
    description: `Easily track and build wealth.\nNo credit card required`,
  }, {
    title: 'Plus',
    mixPanelTitle: 'Plus',
    description: 'Efficient management with enhanced tools',
  }, {
    title: 'Premium',
    mixPanelTitle: 'Premium',
    description: 'Comprehensive tools for complex holdings',
  }, {
    titleFn: ({ isSelected }) => (<div style={{ display: 'flex' }}>Elite <FamilyOfficeSvg color={isSelected ? '#2565E7' : '#1A1B1D'} /></div>),
    mixPanelTitle: 'Elite',
    description: "Full access, personalized services",
  }]

  useEffect(() => {
    billingStore.fetchPricingTable();
    // return () => {}
  }, [billingStore])

  useEffect(() => {
    if (userStore.isTier2 && !userStore.isReadOnlyMode) {
      setSelectedPlanIndex(2);
    } else if ((userStore.isTier3 || userStore.isScoreTier4) && !userStore.isReadOnlyMode) {
      setSelectedPlanIndex(3);
    }
    // return () => {}
  }, [userStore])

  const setSelectedPlan = (plan) => {
    billingStore.setSelectedPlan(plan);
  }

  const handleComparePlansClick = (e) => {
    trackEvent('Pricing Table Clicked', {
      click_type: 'CTA click',
      cta_text: e.target.textContent,
      type: 'button',
      tier: userStore.data?.profile?.tier,
      url: window.location.href,
      recommended_tier: userStore.data?.profile?.scoreTier,
      onboarding_step: userStore.data?.profile?.onBoardingStep,
      payment_status: userStore.data?.profile?.paymentStatus?.join(),
      selected_plan: plans[selectedPlanIndex].mixPanelTitle,
      plan_interval: billingStore.selectedPlan,
    })
    billingStore.setShowComparePlansModal(true);
  }

  const handleContinueClick = async (e, planIndex) => {
    trackEvent('Pricing Table Clicked', {
      click_type: 'CTA click',
      cta_text: e.target.textContent,
      type: 'button',
      tier: userStore.data?.profile?.tier,
      url: window.location.href,
      recommended_tier: userStore.data?.profile?.scoreTier,
      onboarding_step: userStore.data?.profile?.onBoardingStep,
      payment_status: userStore.data?.profile?.paymentStatus?.join(),
      selected_plan: plans[planIndex].mixPanelTitle,
      plan_interval: billingStore.selectedPlan,
      price: mixpanelPriceBySelectedPlan(),
    })
    filtersStore.handleClearFilterSelections();
    if (planIndex === 0) {
      billingStore.setShowStarterModal(true);
    } else {
      const { url } = (userStore.isDemoOrReadOnlyMode || userStore.isFreeTier || userStore.isFreeUser || userStore.isNoneTier || userStore.isManuallyFreeTier) ? await billingStore.fetchSubscriptionUrl(planIndex - 1, window.location.href + '?pricing_modal') : await billingStore.createDeepLink(planIndex - 1);
      window.location.href = url;
    }
  }

  const mixpanelPriceBySelectedPlan = () => {
    if (selectedPlanIndex === 0 && !userStore.isFreeTier) return 0;

    const selectedPlan = billingStore.selectedPlanObj[selectedPlanIndex - 1];
    const isWithDiscount = selectedPlan.hasOwnProperty('originalPriceAfterDiscount') && selectedPlan.originalPriceAfterDiscount !== selectedPlan.originalPrice;
    const price = (isWithDiscount ? selectedPlan.originalPriceAfterDiscount : selectedPlan.originalPrice) / 100;
    const monthsFactor = billingStore.selectedPlan === 'year' ? 12 : 1;

    return price / monthsFactor;
  };

  useTrackEvent('Pricing Table Viewed', {
    tier: userStore.data?.profile?.tier,
    url: window.location.href,
    recommended_tier: userStore.data?.profile?.scoreTier,
    onboarding_step: userStore.data?.profile?.onBoardingStep,
    payment_status: userStore.data?.profile?.paymentStatus?.join(),
  });
  useTrackGTMEvent('Pricing Table Viewed', {
    tier: userStore.data?.profile?.tier,
    url: window.location.href,
    recommended_tier: userStore.data?.profile?.scoreTier,
    onboarding_step: userStore.data?.profile?.onBoardingStep,
    payment_status: userStore.data?.profile?.paymentStatus?.join(),
  });

  const checkScroll = () => {
    if (mobilePricingModalWrapper.current) {
      const { scrollTop, scrollHeight, clientHeight } = mobilePricingModalWrapper.current;
      // Check if footer should be sticky
      const isContentScrollable = scrollHeight > clientHeight;
      setIsFooterSticky(isContentScrollable);
      // Check if header should be sticky
      setIsHeaderSticky(scrollTop > 0);
    }
  };

  useEffect(() => {
    const currentWrapper = mobilePricingModalWrapper.current;
    if (currentWrapper) {
      currentWrapper.addEventListener('scroll', checkScroll);
    }

    // Initial check to set the state correctly on mount
    checkScroll();

    return () => currentWrapper ? currentWrapper.removeEventListener('scroll', checkScroll) : null;
  }, []);

  useEffect(() => {
    const resizeListener = () => {
      if (mobilePricingModalWrapper.current) {
        const { scrollHeight, clientHeight } = mobilePricingModalWrapper.current;
        if (scrollHeight > clientHeight) {
          setIsFooterSticky(true)
        } else {
          setIsFooterSticky(false)
        }
      }
    }
    window.addEventListener("resize", resizeListener);
    return () => window.removeEventListener("resize", resizeListener);
  }, []);

  const handleIntercomOnClick = (e) => {
    startIntercomNewMessage();
  }

  const landingPageOnClick = (e) => {
    window.open('https://vyzer.co/', '_blank');
  }

  const logout = () => {
    if (filtersStore.countActiveFilters) {
      filtersStore.handleClearFilterSelections();
    }
    mixpanelReset();
    userStore.logOut();
  }

  return (
    <Wrapper>
      {!billingStore.isPricingTableLoaded ?
        <div style={{ display: 'grid', placeItems: 'center', flex: 1 }}>
          <LogoLoader />
        </div>
        : uiStore.isMobileAppView ? 
        <MobileAppPricingModal onClose={onClose} />
        // <PageWrapper style={{ width: '100%'}}> 
        //  <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
        //  <MobilePricingModalHeaderWrapper style={{justifyContent:'space-between',}}>
        //       <HeaderLogoAndTextWrapper>
        //         <VyzerDarkSymbol />
        //         Vyzer
        //       </HeaderLogoAndTextWrapper>
            
        //         <ModalCloseButtonWrapper onClick={onClose} style={{ top: '16px' }}><ModalCloseButton /></ModalCloseButtonWrapper>
        //     </MobilePricingModalHeaderWrapper>
        //     <div>Pricing modal mobile app content</div>
        //     </div>
        //   </PageWrapper> 
          :
        <PageWrapper>

          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', }}>
            <MobilePricingModalHeaderWrapper>
              <HeaderLogoAndTextWrapper>
                <LogoWrapper style={{ width: 'auto', boxShadow: 'none' }} href='https://vyzer.co/' target="_blank"
                  onClick={(e) => {
                    if (userStore.isRelevantVariant === 3) {
                      e.preventDefault();
                    }
                  }}
                >
                  <VyzerDarkSymbol />
                </LogoWrapper>
                Choose your plan
              </HeaderLogoAndTextWrapper>
              {userStore.isRelevantVariant === 3 ?
                      <Dropdown
                      overlay={
                        <MobileMenuHoverWrapper>
                          <MobileMenuHoverRow onClick={handleIntercomOnClick}><IntercomSvg /> Chat with us</MobileMenuHoverRow>
                          <MobileMenuHoverRow onClick={landingPageOnClick} style={{ justifyContent: 'space-between' }}><LandingPageSvgWrapper><LandingPageSvg /> Vyzer.co</LandingPageSvgWrapper> <ArrowLinkSvg /></MobileMenuHoverRow>
                          <MobileMenuHoverRow onClick={logout}><LogoutSvg /> Log out</MobileMenuHoverRow>
                        </MobileMenuHoverWrapper>
                      }
                      overlayStyle={{zIndex:9999999999}} trigger={['click']} visible={isMobileMenuOpen}
                      onVisibleChange={(visible) => { setIsMobileMenuOpen(visible) }} >
      
                      <MobileMenuWrapper><MobileMenuSvg bgColor={isMobileMenuOpen ? colors.lightGray2 : '#F4F5F7'}/></MobileMenuWrapper>
                  </Dropdown>
                  :
                <ModalCloseButtonWrapper onClick={onClose} style={{ top: '16px' }}><ModalCloseButton /></ModalCloseButtonWrapper>}
            </MobilePricingModalHeaderWrapper>
            <PlanSwitchWrapper>
              <PlanSwitchOption isSelected={billingStore.selectedPlan === 'year'} onClick={() => { setSelectedPlan('year') }}>Annual billing <SavePercentage isSelected={billingStore.selectedPlan === 'year'}>SAVE 20%</SavePercentage></PlanSwitchOption>
              <PlanSwitchOption isSelected={billingStore.selectedPlan === 'month'} onClick={() => { setSelectedPlan('month') }}>Monthly billing</PlanSwitchOption>
            </PlanSwitchWrapper>
          </div>

          <ModalRightPaneWrapper
            ref={mobilePricingModalWrapper}
            isHeaderSticky={isHeaderSticky}
            isFooterSticky={isFooterSticky}
          >
            <PlansWrapper>
              {plans.map((plan, ind) => {
                const isHiddenFreeTier = (userStore.isRelevantVariant === 1 || (!userStore.isDemoTier && !userStore.isNoneTier)) && ind === 0;
                const isRecommended = ind === recommendedPlanIndex;
                const isSelected = ind === selectedPlanIndex;
                let isWithDiscount = false;
                let relevantPricingObj = {};
                if (ind > 0) {
                  relevantPricingObj = billingStore.selectedPlanObj[ind - 1];
                  isWithDiscount = relevantPricingObj.hasOwnProperty('originalPriceAfterDiscount') && relevantPricingObj.originalPriceAfterDiscount !== relevantPricingObj.originalPrice;
                }
                //For users who purchased Tier 2 but are now being recommended to upgrade to another tier
                const isTierTwoRecommendedPlan = actualTier === 1 && recommendedPlanIndex === 2 && ind === 1 && !userStore.isReadOnlyMode;
                const isTierTwoOrThreeAndRedeemed = ((actualTier === 1 && ind === 1) || (actualTier === 2 && ind === 2)) && userStore.isRedeemed;
                
                const disableRow = isTierTwoRecommendedPlan || isTierTwoOrThreeAndRedeemed;
                
                return (
                  !isHiddenFreeTier ? <PlanRowInvisibleBorder key={ind} onClick={() => { !disableRow && setSelectedPlanIndex(ind) }} isSelected={isSelected} isRecommended={isRecommended} disableRow={disableRow}>
                    {isRecommended ? <RecommendedPlanBanner isSelected={isSelected}>Recommended for you{userStore.isRelevantVariant !== 3 ? `, based on your journey` : ``}</RecommendedPlanBanner> : null}
                    <PlanRowWrapper isRecommended={isRecommended} isSelected={isSelected} displayRowInGray={disableRow} isFirst={ind === 0}>
                      <PlanDetailsWrapper>
                        <PlanSelectionCircle isSelected={isSelected}>
                          {isSelected ? <PlanSelectionCircleFill /> : null}
                        </PlanSelectionCircle>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingRight: '8px' }}>
                          <PlanTitleWrapper isSelected={isSelected}>{plan.titleFn ? plan.titleFn({ isSelected }) : plan.title}</PlanTitleWrapper>
                          <div style={{ lineHeight: '15px', fontSize: '12px', whiteSpace: 'pre-line', textAlign: 'start' }}>{plan.description}</div>
                        </div>
                      </PlanDetailsWrapper>
                      <PlanPriceWrapper isSelected={isSelected}>
                        <PlanPriceValue isSelected={isSelected}>
                          {ind === 0 ? '$0' : displayMoneyValue(Math.ceil((isWithDiscount ? relevantPricingObj.originalPriceAfterDiscount : relevantPricingObj.originalPrice) / 100 / (billingStore.selectedPlan === 'year' ? 12 : 1)), 'USD')}
                        </PlanPriceValue>
                        {isWithDiscount && <PlanDiscountValue isSelected={isSelected}>
                          {ind === 0 ? 'Free' : displayMoneyValue(Math.ceil(relevantPricingObj.originalPrice / 100 / (billingStore.selectedPlan === 'year' ? 12 : 1)), 'USD')}
                        </PlanDiscountValue>}
                        {ind === 0 ? '/ FREE' : '/ mo'}
                      </PlanPriceWrapper>
                      {isWithDiscount ? <div style={{ position: 'absolute', top: '4px', right: '-4px', zIndex: 2 }}><DiscountLabel text={Math.floor(relevantPricingObj.discountPercentage) + '% OFF'} /></div> : null}
                    </PlanRowWrapper>
                  </PlanRowInvisibleBorder> : null
                )
              })}
            </PlansWrapper>
          </ModalRightPaneWrapper>

          <ModalRightPaneFooter isSticky={isFooterSticky}>
            <ContinueButton onClick={(e) => handleContinueClick(e,selectedPlanIndex)}>{billingStore.isLoading ? <LoadingOutlined /> : 'Continue'}</ContinueButton>
            <ComparePlansButton style={{ padding: '6px 0' }} onClick={handleComparePlansClick}>Compare plans</ComparePlansButton>
            {userStore.isRelevantVariant === 1 && <StartExploringFreePlanContainer onClick={(e) => handleContinueClick(e,0)}>Start exploring the free plan</StartExploringFreePlanContainer>}
          </ModalRightPaneFooter>
        </PageWrapper>}
    </Wrapper>
  )
})

const DiscountLabel = ({ text }) => {
  return (
    <DiscountLabelWrapper>
      <svg width="65" height="17" viewBox="0 0 65 17" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip0_4579_113453)">
          <path d="M62.9296 0C63.5982 0 64.2227 0.334167 64.5936 0.890516L65.9999 2.99978C65.9999 2.99978 61.88 4.36719 61.88 3C61.88 1.63281 60.9299 0 60.9299 0H62.9296Z" fill="#B37C3B" />
        </g>
      </svg>
      <DiscountLabelText>{text}</DiscountLabelText>
    </DiscountLabelWrapper>
  )
}

const VyzerDarkSymbol = () => {
  return (
    <svg width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.1942 0H9.86411C4.41631 0 0 4.41631 0 9.86411V22.1942C0 27.642 4.41631 32.0584 9.86411 32.0584H22.1942C27.642 32.0584 32.0584 27.642 32.0584 22.1942V9.86411C32.0584 4.41631 27.642 0 22.1942 0Z" fill="#121D2A" />
      <path d="M10.1234 9.16623L17.7814 22.7358L15.9971 25.8974L6.55469 9.16623H10.1234ZM13.6921 9.16623L15.4588 12.297H20.1038L17.7814 16.4121L19.5658 19.5737V19.5743L25.4398 9.16623H13.6921Z" fill="white" />
    </svg>
  )
}

const FamilyOfficeSvg = ({ color, size }) => {
  return (
    <svg width={size || "20"} height={size || "20"} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M13.1101 8.90597L10 2.76758V2.76767L6.88988 8.90606L3.125 5.22303V13.0802H10V13.0801H16.875V5.22294L13.1101 8.90597Z" fill={color || "#1A1B1D"} />
      <path fillRule="evenodd" clipRule="evenodd" d="M16.1875 16.1905H3.8125V14.5405H16.1875V16.1905Z" fill={color || "#1A1B1D"} />
    </svg>
  )
}

const ModalCloseButton = () => {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="6.28033" y1="5.83393" x2="18.3011" y2="17.8547" stroke="black" strokeWidth="1.5" />
      <line x1="5.92676" y1="17.8544" x2="17.9476" y2="5.83362" stroke="black" strokeWidth="1.5" />
    </svg>
  )
}

const IntercomSvg = () => {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M15.2138 20.2126C17.8012 21.2475 21 22.5402 21 22.5402L20.9993 15.6054V5.04021C20.9993 3.63734 19.955 2.5 18.6671 2.5H4.33225C3.045 2.5 2 3.63804 2 5.03951V17.671C2 19.0738 3.045 20.2126 4.33225 20.2126H15.2138ZM5.85267 13.792C5.74687 13.7573 5.63499 13.744 5.52394 13.7528C5.41289 13.7616 5.30454 13.7924 5.20569 13.8436C5.10735 13.8945 5.02025 13.9647 4.95024 14.0504C4.80683 14.2224 4.73521 14.4419 4.75255 14.665C4.76842 14.8886 4.87317 15.0954 5.04158 15.2438L5.04354 15.2455C5.16111 15.347 7.40565 17.2502 11.5 17.2502C15.595 17.2502 17.8395 15.347 17.9571 15.2455L17.9593 15.2436C18.1274 15.0951 18.2318 14.8883 18.2474 14.6649C18.2648 14.4423 18.1935 14.2231 18.0505 14.0512C17.9803 13.9653 17.893 13.8949 17.7943 13.8439C17.6953 13.7927 17.5867 13.762 17.4754 13.7534C17.3641 13.7447 17.2521 13.7583 17.1462 13.7933C17.0414 13.828 16.9445 13.8831 16.8617 13.9558C16.7984 14.0088 14.9345 15.5499 11.5 15.5499C9.77095 15.5499 8.43243 15.1524 7.52464 14.7539C7.07055 14.5546 6.72414 14.3549 6.49068 14.2046C6.37397 14.1295 6.28556 14.0667 6.22613 14.0225C6.17243 13.9826 6.14277 13.9582 6.13751 13.9538C6.05452 13.8813 5.95749 13.8264 5.85267 13.792Z" fill="#1A1B1D" />
    </svg>
  )
}

const LogoutSvg = () => {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.5 12L10 12" stroke="#1A1B1D" strokeWidth="1.5" />
      <path d="M17 8.5L20.5 12L17 15.5" stroke="#1A1B1D" strokeWidth="1.5" />
      <path d="M11.8605 4.99951L7.36111 4.99951C6.60939 4.99951 6 5.6089 6 6.36062L6 17.6384C6 18.3901 6.60939 18.9995 7.36111 18.9995L12 18.9995" stroke="#1A1B1D" strokeWidth="1.5" />
    </svg>
  )
}

const LandingPageSvg = () => {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 22.193C15.4029 22.193 18.4252 20.5072 20.2521 17.9234L20.2705 17.9049L20.2726 17.9029C20.2845 17.8909 20.3042 17.8713 20.3204 17.8497C20.3308 17.8359 20.3463 17.8128 20.3561 17.7825C21.4914 16.1506 22.1393 14.169 22.1393 12.0269C22.1393 9.87748 21.4603 7.89145 20.3471 6.25867C20.3455 6.25506 20.3439 6.25098 20.342 6.24648C20.3288 6.21458 20.3069 6.16161 20.2523 6.10398C18.3988 3.5201 15.4033 1.86074 12 1.86074C8.59709 1.86074 5.57483 3.54658 3.74795 6.13038L3.72949 6.14884C3.72882 6.1495 3.72813 6.15019 3.72741 6.15091C3.71548 6.16283 3.69579 6.18248 3.67959 6.20408C3.66922 6.2179 3.65366 6.24092 3.64391 6.2713C2.50859 7.90313 1.86074 9.88472 1.86074 12.0269C1.86074 14.1804 2.54229 16.1698 3.65921 17.8044L3.66654 17.8151L3.67118 17.8197C3.67136 17.8202 3.67156 17.8207 3.67178 17.8212C3.67247 17.8229 3.67339 17.8254 3.67453 17.8284C3.67973 17.8421 3.68954 17.8679 3.7034 17.8956L3.71046 17.8921C3.71199 17.904 3.71848 17.9166 3.72632 17.9285L3.72786 17.9302L3.72866 17.931L3.72908 17.9314L3.72928 17.9316L3.72939 17.9317L3.72949 17.9318L3.72989 17.9322L3.73284 17.9352L3.74829 17.9506C5.60182 20.534 8.5971 22.193 12 22.193ZM8.05763 4.13845C7.43394 4.97319 6.92657 6.00638 6.56218 7.16341C6.06624 6.95558 5.62238 6.69613 5.21379 6.42949C5.98212 5.47017 6.95079 4.68091 8.05763 4.13845ZM5.87013 11.2962H3.22625C3.34683 9.9428 3.76458 8.64098 4.43559 7.51928C4.97267 7.87442 5.55527 8.18691 6.22048 8.45656C6.02714 9.35724 5.90333 10.3038 5.87013 11.2962ZM6.19334 15.5183C5.55053 15.7892 4.95183 16.1214 4.43405 16.4782C3.76358 15.3564 3.34621 14.0537 3.22601 12.6769H5.84324C5.87641 13.6702 6.00001 14.6382 6.19334 15.5183ZM8.07221 19.8687C6.95795 19.3254 5.98314 18.5321 5.211 17.567C5.61813 17.2879 6.07938 17.0445 6.56429 16.8375C6.95017 18.0176 7.46196 19.0383 8.07221 19.8687ZM11.3231 20.6954C10.6196 20.5062 9.95213 20.018 9.36287 19.2913C8.75129 18.5371 8.23051 17.5337 7.84822 16.3672C8.91524 16.0573 10.0946 15.8615 11.3231 15.8015V20.6954ZM11.3231 12.65V14.447C9.98992 14.5068 8.68182 14.7072 7.50783 15.0494C7.34695 14.283 7.23122 13.4771 7.1979 12.65H11.3231ZM11.3231 11.3231H7.1979C7.23122 10.4961 7.34694 9.69016 7.5078 8.92388C8.67972 9.26721 9.98758 9.49054 11.3231 9.52848V11.3231ZM11.3231 8.19851C10.0946 8.13853 8.91524 7.94274 7.84822 7.63278C8.23051 6.4663 8.75129 5.46285 9.36287 4.70868C9.95213 3.98203 10.6196 3.49381 11.3231 3.30463V8.19851ZM15.9278 4.13132C17.042 4.67464 18.0169 5.46787 18.789 6.43299C18.3819 6.71209 17.9206 6.95547 17.4357 7.16249C17.0498 5.98244 16.538 4.96165 15.9278 4.13132ZM12.6769 3.30463C13.3804 3.49381 14.0479 3.98203 14.6371 4.70868C15.2487 5.46285 15.7695 6.46629 16.1518 7.63278C15.0848 7.94273 13.9054 8.13853 12.6769 8.19851V3.30463ZM12.6769 11.35V9.55298C14.0101 9.4932 15.3182 9.29278 16.4922 8.95065C16.6531 9.71697 16.7688 10.5229 16.8021 11.35H12.6769ZM12.6769 12.6769H16.8021C16.7688 13.5039 16.6531 14.3098 16.4922 15.0761C15.3203 14.7328 14.0124 14.5095 12.6769 14.4715V12.6769ZM12.6769 20.6954V15.8015C13.9054 15.8615 15.0848 16.0573 16.1518 16.3672C15.7695 17.5337 15.2487 18.5371 14.6371 19.2913C14.0479 20.018 13.3804 20.5062 12.6769 20.6954ZM18.7862 17.5705C18.0179 18.5298 17.0492 19.3191 15.9424 19.8615C16.5661 19.0268 17.0734 17.9936 17.4378 16.8366C17.9338 17.0444 18.3776 17.3039 18.7862 17.5705ZM20.7495 12.7038C20.6524 14.0565 20.2358 15.3584 19.5644 16.4807C19.0273 16.1256 18.4447 15.8131 17.7795 15.5434C17.9729 14.6428 18.0967 13.6962 18.1299 12.7038H20.7495ZM17.8067 8.48184C18.4495 8.21322 19.0505 7.9016 19.5904 7.54652C20.2374 8.6449 20.6538 9.94654 20.774 11.3231H18.1568C18.1236 10.3299 18 9.36192 17.8067 8.48184Z" fill="#1A1B1D" stroke="#1A1B1D" strokeWidth="0.278519" />
    </svg>
  )
}

const ArrowLinkSvg = () => {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13.9727 13.0781C13.9727 13.0781 13.9727 8.81265 13.9727 6.88046C12.0405 6.88046 7.775 6.88046 7.775 6.88046" stroke="#585D66" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6.05273 14.7959L13.6986 7.15005" stroke="#585D66" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

const MobileMenuSvg = ({bgColor}) => {
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="8" y="8" width="28" height="28" rx="14" fill={bgColor || "#F4F5F7"} />
      <circle cx="16.6667" cy="21.9998" r="1.33333" fill="#585D66" />
      <circle cx="22.0001" cy="21.9998" r="1.33333" fill="#585D66" />
      <circle cx="27.3333" cy="21.9998" r="1.33333" fill="#585D66" />
    </svg>
  )
}